import { useMemo, useState, type ReactNode } from 'react';
import { Alert, App, Button, Card, Popconfirm, Progress, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined, DeleteOutlined, GlobalOutlined, ReloadOutlined, ScanOutlined } from '@ant-design/icons';
import { callingCodeOf, countryCodeFindingKey, countryCodeFix, matchCountry, phoneCountries, type ContactCountry, type CountryCodeFinding, type CountryCodeStatus, type CountryCodesApplyResult } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import { EmptyState } from '../../components/states';
import { EntityTag } from '../../components/EntityTag';
import { errorMessage } from '../../api/client';
import { useApplyCountryCodes, useCountryCodesResults, useRecheckCountryCodes, useScanCountryCodes } from '../../features/cleanup/api';
import { fmtDateTime, plural } from '../../utils/format';

interface Row extends CountryCodeFinding {
  key: string;
  contactId: string;
  contactName: string;
  contactEmail: string | null;
  contactCountry: RowCountry | null;
  /** the status from the scan, which the tiles and filter keep using after a country is picked */
  scanStatus: CountryCodeStatus;
}

const STATUS: Record<CountryCodeStatus, { label: string; tile: string; color: string; dot: string; description: string }> = {
  plus: { label: 'Add +', tile: 'Country code without +', color: 'processing', dot: 'medium', description: 'The number has its country code but written as 00… or without the +: it becomes +….' },
  add: { label: 'Add code', tile: 'No country code', color: 'processing', dot: 'medium', description: "A national number that is valid for the contact's country: its country code is added." },
  invalid: { label: 'Not valid', tile: "Doesn't fit the country", color: 'warning', dot: 'high', description: "Not a valid number for the contact's country, so the country code can't be worked out. Check it by hand." },
  'no-country': { label: 'No country', tile: 'Country unknown', color: 'default', dot: 'low', description: 'The contact has no country in its addresses, a Country field, its organization or its other numbers. Pick one in the Country column.' },
};
const ORDER: CountryCodeStatus[] = ['add', 'plus', 'invalid', 'no-country'];

type RowCountry = Omit<ContactCountry, 'source'> & { source: ContactCountry['source'] | 'picked' };

const SOURCE: Record<RowCountry['source'], string> = {
  picked: 'picked by hand',
  address: "from the contact's address",
  field: 'from a Country field',
  organization: "from the organization's address",
  phone: 'from another of its numbers',
};

const countryName = (code: string) => matchCountry(code, { codes: true }) ?? code;
const fixable = (r: Row) => !!r.fixed;

const COUNTRY_OPTIONS = phoneCountries().map((c) => ({ value: c.code, label: `${c.name} ${c.callingCode}` }));
const countryPickerProps = { showSearch: true, optionFilterProp: 'label', options: COUNTRY_OPTIONS, popupMatchSelectWidth: 260 } as const;

/** Country codes tab of the Email & Phone Cleaner: puts the country code on the numbers in each contact's Phones list. */
export function CountryCodesTab({ tabs }: { tabs: ReactNode }) {
  const { message, modal } = App.useApp();
  const stored = useCountryCodesResults();
  const scan = useScanCountryCodes();
  const recheck = useRecheckCountryCodes();
  const apply = useApplyCountryCodes();
  const [progress, setProgress] = useState<{ scanned: number; total: number; found: number } | null>(null);
  const [applied, setApplied] = useState<{ done: number; total: number } | null>(null);
  const [outcome, setOutcome] = useState<CountryCodesApplyResult | null>(null);
  const [status, setStatus] = useState<CountryCodeStatus | 'all'>('all');
  const [country, setCountry] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  /** countries picked by hand, by row key */
  const [picked, setPicked] = useState<Record<string, string>>({});

  const data = stored.data;

  // the scan's answer, worked out again for rows whose country was picked by hand
  const allRows = useMemo<Row[]>(
    () =>
      (data?.results ?? []).flatMap((c) =>
        c.findings.map((f) => {
          const key = countryCodeFindingKey(c.id, f.number);
          const row: Row = { ...f, key, scanStatus: f.status, contactId: c.id, contactName: c.name, contactEmail: c.email, contactCountry: c.country };
          const code = picked[key];
          if (!code) return row;
          const fix = countryCodeFix(f.number, code) ?? { status: f.status, fixed: null, country: null };
          return { ...row, ...fix, contactCountry: { code, callingCode: callingCodeOf(code) ?? '', source: 'picked' as const } };
        }),
      ),
    [data, picked],
  );

  const counts = useMemo(() => {
    const m: Record<CountryCodeStatus, number> = { plus: 0, add: 0, invalid: 0, 'no-country': 0 };
    for (const r of allRows) m[r.scanStatus]++;
    return m;
  }, [allRows]);

  const countryOptions = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of allRows) if (r.contactCountry) m.set(r.contactCountry.code, (m.get(r.contactCountry.code) ?? 0) + 1);
    return [...m]
      .map(([code, n]) => ({ value: code, label: `${countryName(code)} (${n.toLocaleString()})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  const rows = useMemo(() => {
    const needle = q.toLocaleLowerCase();
    return allRows
      .filter((r) => status === 'all' || r.scanStatus === status)
      .filter((r) => !country || r.contactCountry?.code === country)
      .filter((r) => !needle || r.contactName.toLocaleLowerCase().includes(needle) || r.number.includes(needle) || (r.fixed ?? '').includes(needle));
  }, [allRows, status, country, q]);

  const runScan = async () => {
    setSelected([]);
    setOutcome(null);
    setProgress({ scanned: 0, total: 0, found: 0 });
    try {
      stored.set(await scan.mutateAsync({ onProgress: setProgress }));
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setProgress(null);
    }
  };

  /** Fixes (or removes) the rows, then checks their contacts again. */
  const run = async (items: Row[], remove: boolean) => {
    if (!data) return;
    setApplied({ done: 0, total: items.length });
    try {
      const result = await apply.mutateAsync({
        items: items.map((r) => ({ contactId: r.contactId, number: r.number, country: picked[r.key] ?? null, remove })),
        onProgress: (done) => setApplied({ done, total: items.length }),
      });
      setOutcome(result);
      message.success(
        remove
          ? `Removed ${plural(result.removed, 'number')} from ${plural(result.contacts, 'contact')}`
          : `Fixed ${plural(result.fixed, 'number')} on ${plural(result.contacts, 'contact')}`,
      );
      const done = new Set(items.map((r) => r.key));
      setPicked((m) => Object.fromEntries(Object.entries(m).filter(([k]) => !done.has(k))));
      // contacts with nothing left to fix drop out of the results
      const ids = [...new Set(items.map((r) => r.contactId))];
      const fresh = new Map((await recheck.mutateAsync(ids)).map((c) => [c.id, c]));
      stored.set({ ...data, results: data.results.flatMap((c) => (ids.includes(c.id) ? (fresh.has(c.id) ? [fresh.get(c.id)!] : []) : [c])) });
      setSelected((keys) => keys.filter((k) => !done.has(k)));
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setApplied(null);
    }
  };

  const runFix = (target: Row[]) => {
    const items = target.filter(fixable);
    if (!items.length) return;
    modal.confirm({
      title: `Fix ${plural(items.length, 'number')}?`,
      content: <p>Each number is replaced in the contact's Phones list by the form shown under "Becomes". The change shows on each contact's timeline.</p>,
      okText: 'Fix',
      onOk: () => run(items, false),
    });
  };

  const runRemove = (items: Row[]) => {
    if (!items.length) return;
    modal.confirm({
      title: `Remove ${plural(items.length, 'number')}?`,
      content: <p>Each number is taken off the contact's Phones list. The change shows on each contact's timeline.</p>,
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: () => run(items, true),
    });
  };

  const pickCountry = (keys: string[], code: string | undefined) =>
    setPicked((m) => {
      const next = { ...m };
      for (const k of keys) {
        if (code) next[k] = code;
        else delete next[k];
      }
      return next;
    });

  const selectedRows = rows.filter((r) => selected.includes(r.key));
  const percent = progress?.total ? Math.min(99, Math.round((progress.scanned / progress.total) * 100)) : 0;
  const fixableShown = rows.filter(fixable).length;

  return (
    <div className="page">
      <PageHeader
        title="Email & Phone Cleaner"
        subtitle="Put the country code on every number in the contacts' Phones lists, using each contact's country."
        extra={
          <Button type="primary" icon={data ? <ReloadOutlined /> : <ScanOutlined />} onClick={runScan} loading={scan.isPending}>
            {data ? 'Scan again' : 'Scan contacts'}
          </Button>
        }
      />
      {tabs}
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        {progress ? (
          <Card title={<CardTitle title="Scanning" subtitle={`${progress.scanned.toLocaleString()} of ${plural(progress.total, 'contact')} checked · ${plural(progress.found, 'contact')} with numbers to check so far`} />}>
            <Progress percent={percent} status="active" />
          </Card>
        ) : !data ? (
          <Card title={<CardTitle title="How it works" subtitle="Nothing is changed by a scan." />}>
            <Typography.Paragraph>
              The scan works out each contact's country from its address, a Country field, its organization's address or, failing those, another number it
              already has with a country code. It then checks every number in the contact's Phones list:
            </Typography.Paragraph>
            <ul style={{ marginTop: 0 }}>
              <li>numbers that start with <b>+</b> are left as they are;</li>
              <li>
                <b>00357 99 123456</b> and <b>357 99 123456</b> become <b>+357 99 123456</b>;
              </li>
              <li>
                a national number that is valid for the contact's country, such as <b>99 123456</b> for Cyprus, gets the code added: <b>+357 99 123456</b>.
              </li>
            </ul>
            <Typography.Paragraph style={{ marginBottom: 0 }}>Numbers that don't fit the country, or contacts without a known country, are listed so you can fix them by hand.</Typography.Paragraph>
          </Card>
        ) : (
          <>
            <Card
              title={
                <CardTitle
                  title={allRows.length ? `${plural(allRows.length, 'number')} without "+" on ${plural(data.results.length, 'contact')}` : 'Every number has its country code'}
                  subtitle={`${plural(data.scanned, 'contact')} checked ${fmtDateTime(data.finishedAt)}. Click a box to show only those numbers.`}
                />
              }
            >
              {allRows.length ? (
                <div className="issue-grid">
                  {ORDER.filter((s) => counts[s]).map((s) => {
                    const active = status === s;
                    return (
                      <Tooltip key={s} title={STATUS[s].description}>
                        <button type="button" className={`issue-tile${active ? ' issue-tile-active' : ''}`} onClick={() => setStatus(active ? 'all' : s)} aria-pressed={active}>
                          <span className={`issue-dot issue-dot-${STATUS[s].dot}`} />
                          <span className="issue-tile-label">{STATUS[s].tile}</span>
                          <span className="issue-tile-count">{counts[s].toLocaleString()}</span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              ) : (
                <Typography.Text type="secondary">All numbers in the Phones lists start with "+".</Typography.Text>
              )}
            </Card>

            {outcome && outcome.skipped.length > 0 && (
              <Alert
                type="warning"
                showIcon
                closable
                onClose={() => setOutcome(null)}
                title={`Last change: ${plural(outcome.fixed, 'number')} fixed${outcome.removed ? `, ${plural(outcome.removed, 'number')} removed` : ''}, ${plural(outcome.skipped.length, 'number')} skipped.`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {outcome.skipped.slice(0, 20).map((s) => (
                      <li key={`${s.contactId}${s.value}`}>
                        <EntityTag kind="contact" id={s.contactId}>{s.value}</EntityTag>: {s.reason}
                      </li>
                    ))}
                    {outcome.skipped.length > 20 && <li>and {plural(outcome.skipped.length - 20, 'more')}</li>}
                  </ul>
                }
              />
            )}

            {allRows.length > 0 && (
              <Card>
                <div className="table-toolbar">
                  <div className="toolbar-group">
                    <TableSearch value={q} onSearch={setQ} placeholder="Search name or number" width={240} />
                    <Select allowClear showSearch optionFilterProp="label" placeholder="Any country" value={country} onChange={setCountry} options={countryOptions} style={{ minWidth: 200 }} />
                    {status !== 'all' && (
                      <Tag closable onClose={() => setStatus('all')} color="processing">
                        {STATUS[status].tile}
                      </Tag>
                    )}
                  </div>
                  <div className="toolbar-group">
                    {selected.length > 0 && (
                      <>
                        <Select
                          {...countryPickerProps}
                          value={null}
                          placeholder={`Set country for ${plural(selected.length, 'selected')}`}
                          onChange={(code: string) => pickCountry(selected, code)}
                          style={{ width: 240 }}
                          aria-label="Set country for the selected numbers"
                        />
                        <Button danger icon={<DeleteOutlined />} disabled={apply.isPending} onClick={() => runRemove(selectedRows)}>
                          Remove {plural(selectedRows.length, 'selected')}
                        </Button>
                      </>
                    )}
                    <Button
                      type="primary"
                      icon={<GlobalOutlined />}
                      disabled={selected.length ? !selectedRows.some(fixable) : !fixableShown}
                      loading={apply.isPending}
                      onClick={() => runFix(selected.length ? selectedRows : rows)}
                    >
                      {selected.length ? `Fix ${plural(selectedRows.filter(fixable).length, 'selected')}` : `Fix all ${fixableShown.toLocaleString()} shown`}
                    </Button>
                  </div>
                </div>
                {applied && <Progress percent={Math.round((applied.done / applied.total) * 100)} status="active" style={{ marginBottom: 16 }} />}
                <Table<Row>
                  rowKey="key"
                  dataSource={rows}
                  scroll={{ x: 1000 }}
                  rowSelection={{
                    selectedRowKeys: selected,
                    onChange: (keys) => setSelected(keys as string[]),
                    preserveSelectedRowKeys: true,
                  }}
                  pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (n) => plural(n, 'number') }}
                  locale={{ emptyText: <EmptyState description="No numbers match these filters." /> }}
                  columns={[
                    {
                      title: 'Contact',
                      key: 'contact',
                      width: 240,
                      render: (_, r) => <NameCell name={r.contactName || '(no name)'} sub={r.contactEmail ?? 'No email'} to={`/contacts/${r.contactId}`} />,
                    },
                    {
                      title: 'Country',
                      key: 'country',
                      width: 220,
                      render: (_, r) => (
                        <Tooltip title={r.contactCountry ? SOURCE[r.contactCountry.source] : 'Pick the country to add its code'}>
                          <Select
                            {...countryPickerProps}
                            size="small"
                            allowClear={!!picked[r.key]}
                            value={r.contactCountry?.code ?? null}
                            placeholder="Pick country"
                            status={r.contactCountry ? undefined : 'warning'}
                            onChange={(code: string | undefined) => pickCountry([r.key], code)}
                            style={{ width: 200 }}
                            aria-label={`Country for ${r.number}`}
                          />
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'Number',
                      key: 'number',
                      width: 200,
                      render: (_, r) => (
                        <div>
                          <Typography.Text copyable={{ text: r.number }}>{r.number}</Typography.Text>
                          {r.label && (
                            <div>
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {r.label}
                              </Typography.Text>
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: 'Becomes',
                      key: 'fixed',
                      width: 230,
                      render: (_, r) =>
                        r.fixed ? (
                          <Space size={6}>
                            <ArrowRightOutlined style={{ color: '#6B7280' }} />
                            <Typography.Text strong>{r.fixed}</Typography.Text>
                            {r.country && r.country !== r.contactCountry?.code && (
                              <Tooltip title={`This number is from ${countryName(r.country)}`}>
                                <Tag style={{ marginInlineEnd: 0 }}>{r.country}</Tag>
                              </Tooltip>
                            )}
                          </Space>
                        ) : (
                          <Typography.Text type="secondary">-</Typography.Text>
                        ),
                    },
                    {
                      title: 'Status',
                      key: 'status',
                      width: 130,
                      render: (_, r) => (
                        <Tooltip title={STATUS[r.status].description}>
                          <Tag color={STATUS[r.status].color}>{STATUS[r.status].label}</Tag>
                        </Tooltip>
                      ),
                    },
                    {
                      key: 'remove',
                      width: 48,
                      render: (_, r) => (
                        <Popconfirm title="Remove this number from the contact?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => run([r], true)}>
                          <Tooltip title="Remove number">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label={`Remove ${r.number}`} />
                          </Tooltip>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Card>
            )}
          </>
        )}
      </Space>
    </div>
  );
}
